"use client";

import {
  useState,
  useEffect,
  useRef,
  useCallback,
  type ReactNode,
} from "react";
import Link from "next/link";
import { DesktopOrderControllerService } from "@/api";
import { useNotification } from "@/components/NotificationContext";
import { useAuth } from "@/components/login/AuthContext";
import { QRCodeSVG } from "qrcode.react";

type PlanId = "NORMAL" | "PLUGIN";
type BillingCycle = "ONE_MONTH" | "ONE_YEAR";
type PayType = "WECHAT" | "ALIPAY";
type ModalStep = "form" | "qrcode" | "timeout" | "success";

const planConfig: Record<
  PlanId,
  {
    name: string;
    tagline: string;
    popular?: boolean;
    prices: Record<BillingCycle, number>;
    features: ReactNode[];
  }
> = {
  NORMAL: {
    name: "普通版",
    tagline: "满足日常单个视频下载需求",
    prices: { ONE_MONTH: 15.9, ONE_YEAR: 99 },
    features: [
      "网页版单个视频下载，次数无限制",
      "浏览器插件单个视频下载，次数无限制",
      "支持视频下载、实况图视频下载、图标打包下载",
      <>
        PC端蓝猫素材助手无限制使用{" "}
        <a
          href="/download"
          target="_blank"
          className="text-blue-500 hover:underline"
        >
          点击查看
        </a>
      </>,
    ],
  },
  PLUGIN: {
    name: "插件版",
    tagline: "解锁全部功能，下载更高效",
    popular: true,
    prices: { ONE_MONTH: 25, ONE_YEAR: 199 },
    features: ["包含普通版全部权益", "主页批量下载，次数无限制"],
  },
};

const packageTypeMap: Record<PlanId, Record<BillingCycle, string>> = {
  NORMAL: {
    ONE_MONTH: "ONE_MONTH",
    ONE_YEAR: "ONE_YEAR",
  },
  PLUGIN: {
    ONE_MONTH: "PLUGIN_ONE_MONTH",
    ONE_YEAR: "PLUGIN_ONE_YEAR",
  },
};

const cycleLabels: Record<BillingCycle, string> = {
  ONE_MONTH: "包月",
  ONE_YEAR: "包年",
};

const cycleUnits: Record<BillingCycle, string> = {
  ONE_MONTH: "月",
  ONE_YEAR: "年",
};

function fmt(n: number): string {
  const r = Math.round(n * 10) / 10;
  return r % 1 === 0 ? r.toString() : r.toFixed(1);
}

const COUNTDOWN_SECONDS = 300;

function formatCountdown(seconds: number): string {
  const m = Math.floor(seconds / 60)
    .toString()
    .padStart(2, "0");
  const s = (seconds % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}

const CheckIcon = () => (
  <svg
    className="w-5 h-5 text-green-500 shrink-0 mt-0.5"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2.5}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);

export default function SponsorshipContent() {
  const { showNotification } = useNotification();
  const { userData } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalStep, setModalStep] = useState<ModalStep>("form");
  const [planId, setPlanId] = useState<PlanId>("NORMAL");
  const [cycle, setCycle] = useState<BillingCycle>("ONE_YEAR");
  const [payType, setPayType] = useState<PayType>("WECHAT");
  const [contact, setContact] = useState("");
  const [renewAccount, setRenewAccount] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [qrcodeUrl, setQrcodeUrl] = useState<string>("");
  const [orderNo, setOrderNo] = useState<string>("");
  const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS);

  const [isResendOpen, setIsResendOpen] = useState(false);
  const [resendContract, setResendContract] = useState("");
  const [resendNewContract, setResendNewContract] = useState("");
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendErrors, setResendErrors] = useState<Record<string, string>>({});

  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const currentPrice = planConfig[planId].prices[cycle];

  const clearTimers = useCallback(() => {
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const openModal = (plan: PlanId) => {
    setPlanId(plan);
    setIsModalOpen(true);
    setModalStep("form");
    setContact("");
    setRenewAccount("");
    setErrors({});
    setQrcodeUrl("");
    setOrderNo("");
    setCountdown(COUNTDOWN_SECONDS);
  };

  const closeModal = () => {
    clearTimers();
    setIsModalOpen(false);
    setModalStep("form");
  };

  const openResendModal = () => {
    setResendContract("");
    setResendNewContract("");
    setResendErrors({});
    setResendSuccess(false);
    setIsResendOpen(true);
  };

  const closeResendModal = () => {
    setIsResendOpen(false);
    setResendSuccess(false);
  };

  const handleResend = async () => {
    const newErrors: Record<string, string> = {};
    if (!resendContract.trim()) {
      newErrors.contract = "请输入联系方式（邮箱或手机）";
    }
    if (Object.keys(newErrors).length > 0) {
      setResendErrors(newErrors);
      return;
    }

    setResendLoading(true);
    const { data: result } =
      await DesktopOrderControllerService.resendRegisterCodeUsingPost({
        body: {
          contract: resendContract.trim(),
          newContract: resendNewContract.trim() || undefined,
        },
      });
    setResendLoading(false);

    if (result?.code !== "OK") {
      showNotification(result?.message || "重发注册码失败", { type: "error" });
      return;
    }
    setResendSuccess(true);
  };

  const handlePaid = useCallback(() => {
    clearTimers();
    setModalStep("success");
  }, [clearTimers]);

  // Countdown timer
  useEffect(() => {
    if (modalStep !== "qrcode") return;

    countdownRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          if (countdownRef.current) clearInterval(countdownRef.current);
          if (pollRef.current) clearInterval(pollRef.current);
          setModalStep("timeout");
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
    };
  }, [modalStep]);

  // Poll order state
  useEffect(() => {
    if (modalStep !== "qrcode" || !orderNo) return;

    const checkState = async () => {
      try {
        const { data: result } =
          await DesktopOrderControllerService.orderStateUsingGet({
            query: { orderNo },
          });
        const isPaid = result?.code === "OK" && result?.data === "SUCCESS";
        if (isPaid) {
          handlePaid();
        }
      } catch {
        // ignore poll errors
      }
    };

    // Check immediately
    checkState();

    pollRef.current = setInterval(checkState, 3000);

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [modalStep, orderNo, handlePaid]);

  const handleSubmit = async () => {
    const newErrors: Record<string, string> = {};
    if (!userData && !contact.trim()) {
      newErrors.contact = "请输入联系方式（邮箱或手机）";
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    const { data: result } =
      await DesktopOrderControllerService.createOrderUsingPost({
        body: {
          orderType: "PACKAGE",
          packageType: packageTypeMap[planId][cycle],
          customMoney: currentPrice,
          payType,
          remark: contact.trim(),
          renewAccount: renewAccount.trim() || undefined,
        },
      });

    if (result?.code !== "OK" || !result.data) {
      showNotification(result?.message || "创建订单失败", { type: "error" });
      setLoading(false);
      return;
    }

    const { qrcode, orderNo: returnedOrderNo } = result.data;
    setQrcodeUrl(qrcode || "");
    setOrderNo(returnedOrderNo || "");
    setCountdown(COUNTDOWN_SECONDS);
    setModalStep("qrcode");
    setLoading(false);
  };

  return (
    <>
      <div className="min-h-screen bg-gray-50">
        <header className="bg-gradient-to-br from-[#667eea] to-[#764ba2] text-white py-12 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              选择适合你的会员套餐
            </h1>
            <p className="text-xl opacity-90">
              开通会员，高清/2K/4K视频随心下载
            </p>
            <p className="mt-4">
              <Link
                href="/points"
                className="inline-flex items-center gap-1 px-4 py-1.5 rounded-full bg-white/15 hover:bg-white/25 transition-colors text-sm"
              >
                需要字幕提取？前往购买算力 →
              </Link>
            </p>
          </div>
        </header>
        <main className="max-w-4xl mx-auto px-4 py-10">
          {/* 计费周期切换 */}
          <div className="flex justify-center mb-10">
            <div className="inline-flex bg-white rounded-full p-1 border border-gray-200 card-shadow">
              {(Object.keys(cycleLabels) as BillingCycle[]).map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setCycle(c)}
                  className={`px-6 py-2 rounded-full text-sm font-medium transition-all cursor-pointer ${
                    cycle === c
                      ? "bg-gradient-to-r from-[#3b82f6] to-[#2563eb] text-white"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  {cycleLabels[c]}
                  {c === "ONE_YEAR" && (
                    <span
                      className={`ml-1.5 text-xs ${
                        cycle === c ? "text-yellow-200" : "text-red-500"
                      }`}
                    >
                      推荐
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* 套餐卡片 */}
          <div className="grid md:grid-cols-2 gap-6 mb-12">
            {(Object.keys(planConfig) as PlanId[]).map((id) => {
              const plan = planConfig[id];
              const price = plan.prices[cycle];
              const isYearly = cycle === "ONE_YEAR";
              return (
                <div
                  key={id}
                  className={`relative flex flex-col rounded-2xl p-8 transition-all ${
                    plan.popular
                      ? "bg-white border-2 border-[#3b82f6] shadow-lg"
                      : "bg-white border border-gray-200"
                  }`}
                >
                  {plan.popular && (
                    <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-gradient-to-r from-[#3b82f6] to-[#2563eb] text-white text-xs font-medium whitespace-nowrap">
                      最受欢迎
                    </span>
                  )}
                  <h3 className="text-xl font-bold text-gray-900">
                    {plan.name}
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">{plan.tagline}</p>

                  <div className="mt-6 flex items-end gap-2">
                    <span className="text-4xl font-extrabold text-gray-900">
                      {fmt(price)}
                    </span>
                    <span className="text-gray-500 mb-1">
                      元/{cycleUnits[cycle]}
                    </span>
                  </div>
                  {isYearly && (
                    <div className="mt-2 flex items-center gap-2 text-sm">
                      <span className="text-gray-400 line-through">
                        {fmt(plan.prices.ONE_MONTH * 12)}元
                      </span>
                      <span className="text-red-500 font-medium">
                        立省 {fmt(plan.prices.ONE_MONTH * 12 - price)} 元
                      </span>
                      <span className="text-gray-400">
                        折合 {fmt(price / 12)} 元/月
                      </span>
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={() => openModal(id)}
                    className={`mt-6 w-full py-3 rounded-xl font-medium transition-all hover:opacity-90 cursor-pointer ${
                      plan.popular
                        ? "bg-gradient-to-r from-[#3b82f6] to-[#2563eb] text-white"
                        : "bg-gray-900 text-white hover:bg-gray-800"
                    }`}
                  >
                    立即开通
                  </button>

                  <ul className="mt-8 space-y-3">
                    {plan.features.map((feature, i) => (
                      <li
                        key={i}
                        className="flex items-start gap-2.5 text-sm text-gray-600"
                      >
                        <CheckIcon />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>

          <div className="bg-white rounded-2xl card-shadow p-6 leading-loose">
            <div>
              <h2 className="font-bold text-lg mb-2">购买流程</h2>
              <p className="pl-4 text-red-500">
                注：如果没有收到验证码，请
                <Link
                  href="/feedback"
                  className="text-blue-500 hover:underline"
                >
                  联系我们
                </Link>
                或
                <button
                  type="button"
                  onClick={openResendModal}
                  className="text-blue-500 hover:underline cursor-pointer"
                >
                  点击重发
                </button>
                注册码
              </p>
              <p className="pl-4">1.选择合适的套餐，点击「立即开通」</p>
              <p className="pl-4">
                2.填写联系方式并确认支付
                {!userData && (
                  <>
                    ，
                    <span className="text-red-500">
                      注意需要填写联系方式(邮箱或者手机)
                    </span>
                  </>
                )}
              </p>
              <p className="pl-4">
                3.登录后购买，未填写续费账号则默认为当前登录账号续费；填写了登录账号则以
                <span className="text-red-500">填写的账号为准</span>
              </p>
              <p className="pl-4">4.我们将向备注的联系方式发送注册码。</p>
              <p className="pl-4">5.使用注册码，注册成为会员</p>
            </div>
            <div className="mt-6">
              <h2 className="font-bold text-lg mb-2">插件版额外权益</h2>
              <p className="pl-4">1.主页批量下载无限制</p>
            </div>
          </div>
        </main>
      </div>

      {/* 重发注册码弹窗 */}
      {isResendOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          onClick={closeResendModal}
        >
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
          <div
            className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* 头部 */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900">
                重发注册码
              </h2>
              <button
                onClick={closeResendModal}
                className="p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors text-xl leading-none"
              >
                ×
              </button>
            </div>

            {/* 内容 */}
            <div className="px-6 py-5">
              {resendSuccess ? (
                <div className="space-y-5 flex flex-col items-center py-6">
                  <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center">
                    <svg
                      className="w-8 h-8 text-green-500"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <div className="text-center">
                    <h3 className="text-lg font-semibold text-gray-900">
                      注册码已重发
                    </h3>
                    <p className="text-gray-500 mt-1">
                      请注意查收，如仍未收到请
                      <Link
                        href="/feedback"
                        className="text-blue-500 hover:underline"
                      >
                        联系我们
                      </Link>
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={closeResendModal}
                    className="px-6 py-2.5 bg-gradient-to-br from-[#3b82f6] to-[#2563eb] text-white rounded-lg font-medium transition-all hover:opacity-90 cursor-pointer"
                  >
                    确定
                  </button>
                </div>
              ) : (
                <div className="space-y-5">
                  {/* 原联系方式 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      原联系方式
                    </label>
                    <input
                      type="text"
                      value={resendContract}
                      onChange={(e) => {
                        setResendContract(e.target.value);
                        setResendErrors({ ...resendErrors, contract: "" });
                      }}
                      placeholder="请输入下单时填写的邮箱或手机号"
                      className={`w-full px-4 py-2.5 rounded-lg border transition-all duration-200 text-gray-900 placeholder-gray-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 ${
                        resendErrors.contract
                          ? "border-red-500"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                    />
                    {resendErrors.contract && (
                      <p className="text-xs text-red-500 mt-1">
                        {resendErrors.contract}
                      </p>
                    )}
                  </div>

                  {/* 新联系方式 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      新联系方式
                    </label>
                    <input
                      type="text"
                      value={resendNewContract}
                      onChange={(e) => setResendNewContract(e.target.value)}
                      placeholder="如需更换接收邮箱/手机请填写，选填"
                      className="w-full px-4 py-2.5 rounded-lg border border-gray-200 hover:border-gray-300 transition-all duration-200 text-gray-900 placeholder-gray-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                    />
                  </div>

                  {/* 提交按钮 */}
                  <button
                    type="button"
                    onClick={handleResend}
                    disabled={resendLoading}
                    className="w-full py-2.5 bg-gradient-to-br from-[#3b82f6] to-[#2563eb] text-white rounded-lg font-medium transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 cursor-pointer"
                  >
                    {resendLoading && (
                      <svg
                        className="w-5 h-5 animate-spin"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                    )}
                    {resendLoading ? "提交中..." : "确认重发"}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 弹窗 */}
      {isModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          onClick={closeModal}
        >
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
          <div
            className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* 头部 */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900">
                {modalStep === "form" && "确认订单"}
                {modalStep === "qrcode" && "扫码支付"}
                {(modalStep === "timeout" || modalStep === "success") &&
                  "支付结果"}
              </h2>
              <button
                onClick={closeModal}
                className="p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors text-xl leading-none"
              >
                ×
              </button>
            </div>

            {/* 内容 */}
            <div className="px-6 py-5">
              {modalStep === "form" && (
                <div className="space-y-5">
                  {/* 套餐切换 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      选择套餐
                    </label>
                    <div className="flex gap-2">
                      {(Object.keys(planConfig) as PlanId[]).map((id) => (
                        <button
                          key={id}
                          type="button"
                          onClick={() => setPlanId(id)}
                          className={`flex-1 py-2.5 rounded-lg border text-sm font-medium transition-all cursor-pointer ${
                            planId === id
                              ? "border-blue-500 bg-blue-50 text-blue-600"
                              : "border-gray-200 text-gray-600 hover:border-gray-300"
                          }`}
                        >
                          {planConfig[id].name}
                          <span className="block text-lg font-bold">
                            {fmt(planConfig[id].prices[cycle])}元
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* 时长切换 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      选择时长
                    </label>
                    <div className="flex gap-2">
                      {(Object.keys(cycleLabels) as BillingCycle[]).map((c) => (
                        <button
                          key={c}
                          type="button"
                          onClick={() => setCycle(c)}
                          className={`flex-1 py-2.5 rounded-lg border text-sm font-medium transition-all cursor-pointer ${
                            cycle === c
                              ? "border-blue-500 bg-blue-50 text-blue-600"
                              : "border-gray-200 text-gray-600 hover:border-gray-300"
                          }`}
                        >
                          {cycleLabels[c]}
                          <span className="block text-lg font-bold">
                            {fmt(planConfig[planId].prices[c])}元
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* 支付方式 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      支付方式
                    </label>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setPayType("WECHAT")}
                        className={`flex-1 py-2.5 rounded-lg border text-sm font-medium transition-all cursor-pointer ${
                          payType === "WECHAT"
                            ? "border-green-500 bg-green-50 text-green-600"
                            : "border-gray-200 text-gray-600 hover:border-gray-300"
                        }`}
                      >
                        微信支付
                      </button>
                      <button
                        type="button"
                        onClick={() => setPayType("ALIPAY")}
                        className={`flex-1 py-2.5 rounded-lg border text-sm font-medium transition-all cursor-pointer ${
                          payType === "ALIPAY"
                            ? "border-blue-500 bg-blue-50 text-blue-600"
                            : "border-gray-200 text-gray-600 hover:border-gray-300"
                        }`}
                      >
                        支付宝
                      </button>
                    </div>
                  </div>

                  {/* 联系方式 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      {userData ? "联系方式（选填）" : "联系方式"}
                    </label>
                    <input
                      type="text"
                      value={contact}
                      onChange={(e) => {
                        setContact(e.target.value);
                        setErrors({ ...errors, contact: "" });
                      }}
                      placeholder="请输入邮箱或手机号，用于下发注册码"
                      className={`w-full px-4 py-2.5 rounded-lg border transition-all duration-200 text-gray-900 placeholder-gray-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 ${
                        errors.contact
                          ? "border-red-500"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                    />
                    {errors.contact && (
                      <p className="text-xs text-red-500 mt-1">
                        {errors.contact}
                      </p>
                    )}
                  </div>

                  {/* 续费账号 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      续费账号（选填）
                    </label>
                    <input
                      type="text"
                      value={renewAccount}
                      onChange={(e) => setRenewAccount(e.target.value)}
                      placeholder="续费请备注登录账号"
                      className="w-full px-4 py-2.5 rounded-lg border border-gray-200 hover:border-gray-300 transition-all duration-200 text-gray-900 placeholder-gray-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                    />
                  </div>

                  {/* 金额展示 */}
                  <div className="flex items-center justify-between py-3 px-4 bg-gray-50 rounded-lg">
                    <span className="text-gray-600">
                      应付金额（{planConfig[planId].name}·{cycleLabels[cycle]}）
                    </span>
                    <span className="text-2xl font-bold text-red-500">
                      {fmt(currentPrice)}元
                    </span>
                  </div>

                  {/* 提交按钮 */}
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={loading}
                    className="w-full py-2.5 bg-gradient-to-br from-[#3b82f6] to-[#2563eb] text-white rounded-lg font-medium transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 cursor-pointer"
                  >
                    {loading && (
                      <svg
                        className="w-5 h-5 animate-spin"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                    )}
                    {loading ? "创建订单中..." : "确认支付"}
                  </button>
                </div>
              )}

              {modalStep === "qrcode" && (
                <div className="space-y-5 flex flex-col items-center">
                  <div className="text-center">
                    <p className="text-gray-600 mb-1">
                      请使用
                      <span className="font-medium text-gray-900">
                        {payType === "WECHAT" ? "微信" : "支付宝"}
                      </span>
                      扫码支付
                    </p>
                    <p className="text-sm text-gray-500">
                      支付金额：
                      <span className="font-bold text-red-500">
                        {fmt(currentPrice)}元
                      </span>
                    </p>
                  </div>

                  {/* 倒计时 */}
                  <div className="text-center">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-orange-50 text-orange-600 text-sm font-medium">
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      剩余时间 {formatCountdown(countdown)}
                    </span>
                  </div>

                  {/* 二维码 */}
                  {qrcodeUrl ? (
                    <div className="p-3 border border-gray-200 rounded-xl bg-white">
                      <QRCodeSVG
                        value={qrcodeUrl}
                        size={224}
                        level="M"
                        includeMargin
                      />
                    </div>
                  ) : (
                    <div className="w-56 h-56 flex items-center justify-center border border-gray-200 rounded-xl bg-gray-50 text-gray-400">
                      加载中...
                    </div>
                  )}

                  <p className="text-xs text-gray-400 text-center">
                    二维码有效期5分钟，过期后请重新下单
                  </p>
                </div>
              )}

              {modalStep === "timeout" && (
                <div className="space-y-5 flex flex-col items-center py-6">
                  <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center">
                    <svg
                      className="w-8 h-8 text-red-500"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <div className="text-center">
                    <h3 className="text-lg font-semibold text-gray-900">
                      支付超时
                    </h3>
                    <p className="text-gray-500 mt-1">
                      支付二维码已过期，请重新下单支付
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setModalStep("form");
                      setCountdown(COUNTDOWN_SECONDS);
                    }}
                    className="px-6 py-2.5 bg-gradient-to-br from-[#3b82f6] to-[#2563eb] text-white rounded-lg font-medium transition-all hover:opacity-90 cursor-pointer"
                  >
                    重新下单
                  </button>
                </div>
              )}

              {modalStep === "success" && (
                <div className="space-y-5 flex flex-col items-center py-6">
                  <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center">
                    <svg
                      className="w-8 h-8 text-green-500"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <div className="text-center">
                    <h3 className="text-lg font-semibold text-gray-900">
                      支付成功，注册码已发送
                    </h3>
                    <p className="text-gray-500 mt-1">
                      感谢你的支持，会员权益已生效！
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-6 py-2.5 bg-gradient-to-br from-[#3b82f6] to-[#2563eb] text-white rounded-lg font-medium transition-all hover:opacity-90 cursor-pointer"
                  >
                    确定
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
